import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

const LOGO_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_aac32766-5fa5-4a83-81c9-03bafeaebc9e/artifacts/bi1rz20k_loyerbook.png';

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl sm:text-2xl font-bold text-[#0D1B2A]">{title}</h2>
      <div className="mt-3 text-sm text-slate-700 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div data-testid="page-privacy" className="min-h-screen bg-white text-[#0D1B2A]">
      <header className="max-w-4xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A]">
          <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
        </Link>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="LoyerBook" className="h-10 w-10" />
          <div className="leading-tight">
            <div className="text-lg font-extrabold">LoyerBook</div>
            <div className="text-[10px] text-slate-500">Le carnet de loyer numérique</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 pb-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700">
          <ShieldCheck className="w-3 h-3" /> Politique en vigueur — dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
        </div>
        <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">Politique de confidentialité</h1>
        <p className="mt-3 text-sm text-slate-600">
          LoyerBook (« nous ») respecte la vie privée de ses utilisateurs. Cette politique décrit comment nous
          collectons, utilisons, stockons et protégeons vos données personnelles.
        </p>

        <Section title="1. Données collectées">
          <p>Lorsqu&apos;un compte est créé pour vous par un agent ou par le Platform Owner, nous collectons :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Identité : nom, prénom, adresse email, numéro de téléphone.</li>
            <li>Adresse postale et pays de résidence.</li>
            <li>Pièce d&apos;identité (recto/verso) pour les bailleurs — stockée sous forme chiffrée.</li>
            <li>Données transactionnelles : paiements enregistrés, échéances, reçus.</li>
            <li>Journaux techniques (adresse IP, horodatages) à des fins de sécurité.</li>
          </ul>
        </Section>

        <Section title="2. Finalités">
          <ul className="list-disc pl-6 space-y-1">
            <li>Fournir le service de gestion de loyers.</li>
            <li>Générer et archiver les reçus et documents officiels.</li>
            <li>Assurer la sécurité des comptes (authentification, détection de fraude).</li>
            <li>Respecter nos obligations légales (comptabilité, lutte anti-fraude).</li>
          </ul>
        </Section>

        <Section title="3. Base légale">
          <p>Le traitement repose sur : votre <strong>consentement</strong> (recueilli lors de la création du compte),
          l&apos;exécution du contrat de service, et nos obligations légales.</p>
        </Section>

        <Section title="4. Durée de conservation">
          <p>Les données de compte sont conservées tant que le compte est actif. Après clôture, elles sont
          conservées 5 ans pour des raisons comptables et légales, puis supprimées ou anonymisées.</p>
        </Section>

        <Section title="5. Sécurité">
          <ul className="list-disc pl-6 space-y-1">
            <li>Chiffrement des mots de passe (bcrypt avec facteur de coût élevé).</li>
            <li>Chiffrement Fernet (AES-128) des pièces d&apos;identité.</li>
            <li>Communications sécurisées via HTTPS/TLS.</li>
            <li>Authentification à deux facteurs (TOTP) obligatoire pour le Platform Owner.</li>
            <li>Journal d&apos;audit horodaté de toutes les actions sensibles.</li>
            <li>Limitation stricte des tentatives de connexion.</li>
          </ul>
        </Section>

        <Section title="6. Vos droits (RGPD)">
          <p>Vous disposez à tout moment des droits suivants :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Accès</strong> — obtenir une copie de vos données.</li>
            <li><strong>Rectification</strong> — corriger vos données inexactes.</li>
            <li><strong>Effacement</strong> — demander l&apos;anonymisation de votre compte.</li>
            <li><strong>Opposition</strong> — refuser certains traitements non essentiels.</li>
            <li><strong>Portabilité</strong> — récupérer vos données au format machine.</li>
          </ul>
          <p>Pour exercer ces droits, contactez : <a href="mailto:privacy@loyerbook.com" className="text-[#1677FF] underline">privacy@loyerbook.com</a>.</p>
        </Section>

        <Section title="7. Partage des données">
          <p>Vos données ne sont jamais vendues. Elles peuvent être partagées avec :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Prestataires techniques (hébergement, envoi d&apos;emails/SMS), sous contrat de confidentialité.</li>
            <li>Autorités compétentes, uniquement sur requête légale.</li>
          </ul>
        </Section>

        <Section title="8. Cookies">
          <p>LoyerBook utilise des cookies techniques essentiels au fonctionnement (session, préférences).
          Aucun cookie de suivi publicitaire n&apos;est utilisé.</p>
        </Section>

        <Section title="9. Contact">
          <p>Pour toute question : <a href="mailto:privacy@loyerbook.com" className="text-[#1677FF] underline">privacy@loyerbook.com</a>.</p>
        </Section>
      </main>
    </div>
  );
}
